import { useState } from 'react';
import { Button } from '@/components/common';
import { CityAutocomplete } from '@/components/forms';
import { RELATIONS } from '@/constants';
import { ReferidoNavigator } from './ReferidoNavigator';
import type { ReferidoFormData, RelationType } from '@/types';

interface PhaseDetailsProps {
  referidos: ReferidoFormData[];
  currentIndex: number;
  onUpdateReferido: (index: number, updates: Partial<ReferidoFormData>) => void;
  onNavigate: (index: number) => void;
  onComplete: () => void;
  onBack: () => void;
}

export function PhaseDetails({
  referidos,
  currentIndex,
  onUpdateReferido,
  onNavigate,
  onComplete,
  onBack,
}: PhaseDetailsProps) {
  const current = referidos[currentIndex];
  const [showWarning, setShowWarning] = useState(false);

  if (!current) return null;

  // Un contacto está "completo" si tiene ciudad y relación.
  const incompletos = referidos.filter((r) => !r.ciudad || !r.relacion);
  const filledCount = referidos.length - incompletos.length;

  // Empujoncito amable: si faltan datos, avisamos pero NO bloqueamos.
  const handleContinue = () => {
    if (incompletos.length > 0) {
      setShowWarning(true);
    } else {
      onComplete();
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Detalles de cada referido
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Completa la información. {filledCount}/{referidos.length} completados
        </p>
      </div>

      {/* Navigator */}
      <ReferidoNavigator
        currentIndex={currentIndex}
        total={referidos.length}
        onPrev={() => onNavigate(currentIndex - 1)}
        onNext={() => onNavigate(currentIndex + 1)}
        currentName={current.nombre + (current.apellido ? ` ${current.apellido}` : '')}
      />

      {/* Detail fields */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        {/* Read-only info */}
        <div className="flex items-center gap-2 flex-wrap">
          {current.telefono && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
              {current.telefono.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
            </span>
          )}
          {current.mensaje_enviado && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              {current.mensaje_enviado === 'WHATSAPP' ? 'WhatsApp' : current.mensaje_enviado === 'IMESSAGE' ? 'iMessage' : current.mensaje_enviado === 'CITA_INSTANTANEA' ? 'Llamada instantánea' : 'SMS'}
            </span>
          )}
          {/* Responses are captured in a dedicated phase */}
        </div>

        {/* Relationship */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relación con el referidor <span className="text-danger-500">*</span>
          </label>
          <select
            value={current.relacion || ''}
            onChange={(e) =>
              onUpdateReferido(currentIndex, {
                relacion: e.target.value as RelationType,
              })
            }
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="">Seleccionar...</option>
            {RELATIONS.map((rel) => (
              <option key={rel.value} value={rel.value}>
                {rel.label}
              </option>
            ))}
          </select>
        </div>

        {/* City */}
        <CityAutocomplete
          value={current.ciudad || ''}
          onChange={(value) => onUpdateReferido(currentIndex, { ciudad: value })}
          required
        />

        {/* Works? */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ¿Trabaja?
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onUpdateReferido(currentIndex, { trabaja: 'SI' })}
              className={`flex-1 py-2.5 rounded-lg border-2 font-medium transition-colors ${current.trabaja === 'SI'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600'
                }`}
            >
              Sí
            </button>
            <button
              type="button"
              onClick={() => onUpdateReferido(currentIndex, { trabaja: 'NO' })}
              className={`flex-1 py-2.5 rounded-lg border-2 font-medium transition-colors ${current.trabaja === 'NO'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600'
                }`}
            >
              No
            </button>
          </div>
        </div>

        {/* Extra notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comentarios
          </label>
          <textarea
            value={current.dato_extra || ''}
            onChange={(e) =>
              onUpdateReferido(currentIndex, { dato_extra: e.target.value })
            }
            placeholder="Ej: Le gusta cocinar, tiene 3 hijos, trabaja desde hace 3 meses"
            maxLength={300}
            rows={3}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 placeholder:text-gray-400 text-gray-900 resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-1">
            {(current.dato_extra || '').length}/300
          </p>
        </div>
      </div>

      {/* Aviso suave: faltan datos (NO bloquea) */}
      {showWarning && incompletos.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3">
          <p className="text-sm text-amber-800">
            A {incompletos.length} contacto{incompletos.length !== 1 ? 's' : ''} le
            {incompletos.length !== 1 ? 's' : ''} falta <strong>ciudad</strong> o{' '}
            <strong>relación</strong>. ¿Quieres revisarlos o seguir de todos modos?
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowWarning(false)}
              className="flex-1"
            >
              Revisar
            </Button>
            <Button onClick={onComplete} className="flex-1">
              Seguir igual
            </Button>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3 pt-2">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          Volver
        </Button>
        <Button onClick={handleContinue} className="flex-1">
          Continuar a Respuestas
        </Button>
      </div>
    </div>
  );
}
